import type { LucideIcon } from 'lucide-react';
import { ArrowRight, BookOpen, CheckCircle2, Database } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { JsonLd, breadcrumbJsonLd } from '@/components/JsonLd';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import { SITE_LOCALE, SITE_ORIGIN, HREFLANG_TAGS } from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

export interface IntegrationCapability {
  key: string;
  icon: LucideIcon;
}

export interface IntegrationPageProps {
  /** i18n namespace prefix, e.g. 'topdeskPage'. */
  ns: string;
  /** Route path, e.g. '/integrations/topdesk'. */
  path: string;
  heroIcon: LucideIcon;
  accentGradient: string;
  iconColor: string;
  iconBg: string;
  capabilities: IntegrationCapability[];
  /** Number of `{ns}.steps.{i}` connect steps (1-based). */
  stepCount: number;
  /** Number of `{ns}.data.{i}` data items (1-based). */
  dataItemCount: number;
  faqKeys: string[];
  docsHref: string;
  eventPrefix: string;
  relatedLinks: { label: string; href: string }[];
}

/**
 * Marketing page for a single integration ("connect X to LogPulse"), driven by
 * i18n keys under `ns` so it exists localized on every locale domain — the
 * Dutch TOPdesk page on logpulse.nl is the point. Follows the pattern that
 * wins these SERPs (see docs/daily/2026-07-04.md): how it connects, what you
 * can detect, and an FAQ block, with the EU-hosting line no US competitor
 * leads with.
 */
export function IntegrationPage({
  ns,
  path,
  heroIcon: HeroIcon,
  accentGradient,
  iconColor,
  iconBg,
  capabilities,
  stepCount,
  dataItemCount,
  faqKeys,
  docsHref,
  eventPrefix,
  relatedLinks,
}: IntegrationPageProps) {
  const t = useT();

  useDocumentMeta({
    title: t(`${ns}.meta.title`),
    description: t(`${ns}.meta.description`),
  });

  const pageUrl = `${SITE_ORIGIN}${path}`;
  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t(`${ns}.meta.title`),
    description: t(`${ns}.meta.description`),
    url: pageUrl,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    isPartOf: { '@type': 'WebSite', name: 'LogPulse', url: SITE_ORIGIN },
    about: { '@id': `${SITE_ORIGIN}/#software` },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    mainEntity: faqKeys.map((k) => ({
      '@type': 'Question',
      name: t(`${ns}.faq.${k}.q`),
      acceptedAnswer: { '@type': 'Answer', text: t(`${ns}.faq.${k}.a`) },
    })),
  };

  const breadcrumb = breadcrumbJsonLd([
    { name: 'LogPulse', url: SITE_ORIGIN },
    { name: t(`${ns}.hero.title`), url: pageUrl },
  ]);

  const steps = Array.from({ length: stepCount }, (_, i) => i + 1);
  const dataItems = Array.from({ length: dataItemCount }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <JsonLd data={webPageJsonLd} />
      <JsonLd data={breadcrumb} />
      <JsonLd data={faqJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 gradient-mesh overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className={cn('inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br items-center justify-center mb-6 shadow-md', accentGradient)}>
            <HeroIcon className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-balance">
            <span className="text-gradient">{t(`${ns}.hero.title`)}</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-balance">
            {t(`${ns}.hero.subtitle`)}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://app.logpulse.io/sign-up"
              className={buttonVariants({ variant: 'gradient', size: '2xl' })}
              onClick={() => trackEvent(`${eventPrefix}_cta_click`, { cta: 'primary' })}
            >
              {t(`${ns}.hero.cta`)}
              <ArrowRight className="h-5 w-5 ml-1" />
            </a>
            <LocalizedAnchor
              href={docsHref}
              className={buttonVariants({ variant: 'outline', size: '2xl' })}
              onClick={() => trackEvent(`${eventPrefix}_cta_click`, { cta: 'docs' })}
            >
              <BookOpen className="h-4 w-4 mr-1.5" />
              {t(`${ns}.hero.ctaSecondary`)}
            </LocalizedAnchor>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
              {t(`${ns}.capabilities.title`)}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
              {t(`${ns}.capabilities.subtitle`)}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.key}
                  className="glass-subtle rounded-2xl p-6 sm:p-7 border border-gray-200/60 dark:border-gray-800/60"
                >
                  <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', iconBg)}>
                    <Icon className={cn('h-6 w-6', iconColor)} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">
                    {t(`${ns}.capabilities.${c.key}.title`)}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`${ns}.capabilities.${c.key}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it connects */}
      <section className="py-20 sm:py-24 bg-gray-50 dark:bg-gray-900/40 border-y border-gray-200/60 dark:border-gray-800/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-center mb-12 text-balance">
            {t(`${ns}.steps.title`)}
          </h2>
          <ol className="space-y-6">
            {steps.map((i) => (
              <li key={i} className="flex gap-5">
                <span className={cn('flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold bg-gradient-to-br', accentGradient)}>
                  {i}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t(`${ns}.steps.${i}.title`)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`${ns}.steps.${i}.description`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Data that lands in LogPulse */}
      <section className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-center mb-4 text-balance">
            {t(`${ns}.data.title`)}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 text-center max-w-2xl mx-auto mb-10 text-balance">
            {t(`${ns}.data.subtitle`)}
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {dataItems.map((i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-700 dark:text-gray-300"
              >
                <Database className={cn('h-4 w-4 mt-0.5 flex-shrink-0', iconColor)} />
                {t(`${ns}.data.${i}`)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-t border-gray-200/60 dark:border-gray-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-center mb-12 text-balance">
            {t(`${ns}.faq.title`)}
          </h2>
          <dl className="space-y-8">
            {faqKeys.map((k) => (
              <div key={k}>
                <dt className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(`${ns}.faq.${k}.q`)}
                </dt>
                <dd className="mt-2 text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`${ns}.faq.${k}.a`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4 sm:pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 text-sm">
            <span className="font-semibold text-gray-900 dark:text-white">{t('related.title')}</span>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {relatedLinks.map((r) => (
                <LocalizedAnchor
                  key={r.href}
                  href={r.href}
                  className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  {r.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </LocalizedAnchor>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 gradient-subtle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 text-balance">
            {t(`${ns}.cta.title`)}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 text-balance">
            {t(`${ns}.cta.body`)}
          </p>
          <a
            href="https://app.logpulse.io/sign-up"
            className={buttonVariants({ variant: 'gradient', size: '2xl' })}
            onClick={() => trackEvent(`${eventPrefix}_cta_click`, { cta: 'final' })}
          >
            {t(`${ns}.cta.button`)}
            <ArrowRight className="h-5 w-5 ml-1" />
          </a>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-500">
            <CheckCircle2 className="h-4 w-4 text-success-500" />
            <span>{t(`${ns}.cta.note`)}</span>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
